"use client";

import React, { useMemo } from "react";
import { icons } from "@workspace/ui/icons";
import Logo from "@workspace/ui/icons/logo";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { useModal } from "@/components/providers/modal-provider";
import { useSearchParams } from "next/navigation";
import {
  defaultFooterFootnotes,
  type FooterLink,
  type MainFooterContent,
} from "../../_utils/footer";
import { FooterFootnotes } from "./footer-footnotes";
import {
  EventNames,
  SectionTypes,
  sendEvent,
  withSectionType,
} from "@/lib/utils/analytics";

export default function Footer({
  footer,
  className,
  footnotes = defaultFooterFootnotes,
}: {
  footer: MainFooterContent;
  className?: string;
  footnotes?: readonly string[];
}): React.ReactElement {
  const { handleOpenModal } = useModal();
  const searchParams = useSearchParams();
  const year = useMemo(() => new Date().getFullYear(), []);

  const getHref = (link: string) => {
    if (link.includes("blog")) return link;
    return `${link}?${searchParams.toString()}`;
  };

  const handlePrivacyChoices = (link: FooterLink) => {
    if (link.type === "privacy") {
      const cookie =
        document.getElementById("cookie-floating-button") ||
        document.getElementById("customise-cookie");
      cookie?.click();
      handleOpenModal("cookie-modal");
    }
  };

  return (
    <footer
      className={cn(
        "bg-black md:bg-base-13 px-7 pt-20 pb-10 md:px-[60px] md:pt-[90px] md:pb-[60px]",
        className,
      )}
      id="footer-container"
      data-testid="footer-container"
    >
      <div
        className="max-w-[1440px] mx-auto"
        id="footer-inner"
        data-testid="footer-inner"
      >
        <FooterFootnotes items={footnotes} variant="dark" />
        {/* ── Desktop two-column row ── */}
        <div
          className="flex flex-col md:flex-row md:gap-16"
          id="footer-sections"
          data-testid="footer-sections"
        >
          {/* Left column — socials + large wordmark (desktop only) */}
          <div
            className="hidden md:flex md:flex-col md:justify-between md:gap-[45px] md:flex-1"
            id="footer-brand-desktop"
            data-testid="footer-brand-desktop"
          >
            <div
              className="flex gap-[15px] items-center"
              id="footer-socials-desktop"
              data-testid="footer-socials-desktop"
            >
              {footer.socials.map((social, sIdx: number) => {
                const Icon = icons[social.name]!;
                return (
                  <Link
                    href={social.url}
                    key={social.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    id={`footer-social-desktop-${sIdx + 1}`}
                    data-testid={`footer-social-desktop-${sIdx + 1}`}
                  >
                    <Icon
                      className="brightness-0 invert"
                      id={`footer-social-desktop-${sIdx + 1}-icon`}
                      data-testid={`footer-social-desktop-${sIdx + 1}-icon`}
                    />
                  </Link>
                );
              })}
            </div>
            <Logo
              className="text-[#F3F2F1] w-full h-auto"
              id="footer-logo-desktop"
              data-testid="footer-logo-desktop"
            />
          </div>

          {/* Right column — nav columns + copyright */}
          <div
            className="flex flex-col md:flex-1 md:justify-between md:items-end md:gap-[45px]"
            id="footer-nav"
            data-testid="footer-nav"
          >
            {/* Nav columns */}
            <div
              className="flex flex-col gap-10 md:flex-row md:gap-10 md:w-full"
              id="footer-nav-columns"
              data-testid="footer-nav-columns"
            >
              {footer.columns.map((column, colIdx: number) => (
                <div
                  key={column.title}
                  className="flex flex-col gap-5 md:flex-1 md:gap-3"
                  id={`footer-nav-column-${colIdx + 1}`}
                  data-testid={`footer-nav-column-${colIdx + 1}`}
                >
                  <p
                    className="text-[12px] font-semibold uppercase leading-[1.25em] tracking-[0.24px] text-[#F3F2F1] opacity-60"
                    id={`footer-nav-column-${colIdx + 1}-title`}
                    data-testid={`footer-nav-column-${colIdx + 1}-title`}
                  >
                    {column.title}
                  </p>
                  <div
                    className="flex flex-col gap-[14px] md:gap-[9px]"
                    id={`footer-nav-column-${colIdx + 1}-links`}
                    data-testid={`footer-nav-column-${colIdx + 1}-links`}
                  >
                    {column.links.map((link, linkIdx: number) => (
                      <div
                        key={linkIdx}
                        id={`footer-nav-column-${colIdx + 1}-link-${linkIdx + 1}`}
                        data-testid={`footer-nav-column-${colIdx + 1}-link-${linkIdx + 1}`}
                      >
                        {link.url ? (
                          <Link
                            href={getHref(link.url)}
                            className="text-base font-normal leading-[1.5em] tracking-[-0.4px] text-[#F3F2F1] hover:opacity-80 transition-opacity"
                            id={`footer-nav-column-${colIdx + 1}-link-${linkIdx + 1}-anchor`}
                            data-testid={`footer-nav-column-${colIdx + 1}-link-${linkIdx + 1}-anchor`}
                            onClick={() =>
                              sendEvent(
                                EventNames.FOOTER_LINK_CLICKED,
                                withSectionType(SectionTypes.FOOTER, {
                                  item_name: link.text,
                                }),
                              )
                            }
                          >
                            <span
                              id={`footer-nav-column-${colIdx + 1}-link-${linkIdx + 1}-text`}
                              data-testid={`footer-nav-column-${colIdx + 1}-link-${linkIdx + 1}-text`}
                            >
                              {link.text}
                            </span>
                          </Link>
                        ) : (
                          <span
                            className={cn(
                              "text-base font-normal leading-[1.5em] tracking-[-0.4px] text-[#F3F2F1] transition-opacity",
                              link.type === "privacy" &&
                                "cursor-pointer hover:opacity-80",
                            )}
                            id={`footer-nav-column-${colIdx + 1}-link-${linkIdx + 1}-text`}
                            data-testid={`footer-nav-column-${colIdx + 1}-link-${linkIdx + 1}-text`}
                            role={
                              link.type === "privacy" ? "button" : undefined
                            }
                            onClick={() => handlePrivacyChoices(link)}
                          >
                            {link.text}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Copyright — desktop right-aligned, hidden on mobile (shown below) */}
            <p
              className="hidden md:block text-sm font-normal text-[#857E77] tracking-[-0.3px] self-end"
              id="footer-copyright-desktop"
              data-testid="footer-copyright-desktop"
            >
              © {year}, Inito Inc. All Rights Reserved.
            </p>
          </div>
        </div>

        {/* ── Mobile: logo + socials + copyright + large wordmark ── */}
        <div
          className="md:hidden mt-10 flex flex-col gap-10"
          id="footer-brand-mobile"
          data-testid="footer-brand-mobile"
        >
          {/* Socials row */}
          <div
            className="flex gap-[15px] items-center"
            id="footer-socials-mobile"
            data-testid="footer-socials-mobile"
          >
            {footer.socials.map((social, sIdx: number) => {
              const Icon = icons[social.name]!;
              return (
                <Link
                  href={social.url}
                  key={social.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  id={`footer-social-mobile-${sIdx + 1}`}
                  data-testid={`footer-social-mobile-${sIdx + 1}`}
                >
                  <Icon
                    className="brightness-0 invert"
                    id={`footer-social-mobile-${sIdx + 1}-icon`}
                    data-testid={`footer-social-mobile-${sIdx + 1}-icon`}
                  />
                </Link>
              );
            })}
          </div>

          {/* Copyright */}
          <p
            className="text-sm font-normal text-[#857E77] tracking-[-0.3px]"
            id="footer-copyright-mobile"
            data-testid="footer-copyright-mobile"
          >
            © {year}, Inito Inc. All Rights Reserved.
          </p>

          {/* Large INITO wordmark */}
          <Logo
            className="text-[#F3F2F1] w-full h-auto"
            id="footer-logo-large-mobile"
            data-testid="footer-logo-large-mobile"
          />
        </div>
      </div>
    </footer>
  );
}
